import React, { useMemo } from 'react';
import * as cheerio from 'cheerio';
import { useGetAllSpamWords } from '../../../query/spam-word';
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';
import { getSpamCheckResult } from '../../../containers/Mail/Templates/TemplateEditorModal/spammyChecker';
import type { SpamCheckResult } from '../../../containers/Mail/Templates/TemplateEditorModal/spammyChecker';
import { Tag } from 'antd';
import _ from 'lodash';
import { SPAM_WORD_CATEGORY } from '../../../containers/Mail/SpamWords/utils';
import tinycolor from 'tinycolor2';
import { getEnv } from '../../../services/env-service';

const highlightStyleString = `<style>
.misspelled { border-bottom: 1px solid red; margin-bottom: -1px; }
.urgency { background-color: #f77171 }
.shady { background-color: #f8a9d4 }
.overpromise { background-color: #fcba74 }
.money { background-color: #fcd34c }
.unnatural { background-color: #d1d5db } 
.spammy { border-radius: 2px }
</style>
`;

const getTextNodes = (elem, $) =>
  elem.type === 'text'
    ? []
    : elem
        .contents()
        .toArray()
        .filter(el => el !== undefined) //I don't know why some elements are undefined
        .reduce(
          (acc, el) =>
            acc.concat(...(el.type === 'text' ? [el] : getTextNodes($(el), $))),
          [],
        );

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

function addMisspellClassToHtml(html: string, misspellingWords: string[]) {
  const $ = cheerio.load(html);
  misspellingWords.forEach(word => {
    const replaceRegex = new RegExp(escapeRegExp(word), 'g');
    getTextNodes($(`html`), $)
      .filter(node => $.html(node).match(replaceRegex))
      .map(node =>
        $(node).replaceWith(
          $.html(node).replace(
            replaceRegex,
            `<span class="misspelled">${word}</span>`,
          ),
        ),
      );
  });
  $('body').prepend(highlightStyleString);
  return $.html();
}

function addSpammyWordCheckToHtml(
  html: string,
  spamCheckResult: SpamCheckResult,
) {
  const $ = cheerio.load(html);
  spamCheckResult.spamWords.forEach(word => {
    const replaceRegex = new RegExp(
      `\\b${escapeRegExp(word.word.trim())}\\b`,
      'gi',
    );
    getTextNodes($(`html`), $)
      .filter(node => $.html(node).match(replaceRegex))
      .map(node =>
        $(node).replaceWith(
          $.html(node).replace(replaceRegex, str => {
            return `<span class="spammy ${word.category.toLowerCase()}">${str}</span>`;
          }),
        ),
      );
  });
  return $.html();
}

function addUnsubscriptionLinkToHtml(
  html: string,
  bypassUnsubscription: boolean,
) {
  if (bypassUnsubscription) return html;
  const $ = cheerio.load(html);
  const unsubscriptionUrl =
    getEnv('REACT_APP_UNSUBSCRIPTION_FE_URL') ||
    'https://unsubscription.turing.com/next';
  $('body').append(
    `<p style='text-align: center'> Please use this link to ${`<a href="${unsubscriptionUrl}">unsubscribe</a>`} from all our emails.</p>`,
  );
  return $.html();
}

type Props = {
  html: string,
  misspellingWords: string[],
  bypassUnsubscription?: boolean,
};

const RenderHTML = ({
  html = '',
  misspellingWords = [],
  bypassUnsubscription,
  ...props
}: Props) => {
  const { data = [] } = useGetAllSpamWords();
  const iframe = React.useRef();
  const spamCheckResult = getSpamCheckResult(html, data);
  useDeepCompareEffect(() => {
    const htmlWithSpellCheck = addMisspellClassToHtml(html, misspellingWords),
      htmlWithSpamCheck = addSpammyWordCheckToHtml(
        htmlWithSpellCheck,
        spamCheckResult,
      ),
      htmlWithUnsubscription = addUnsubscriptionLinkToHtml(
        htmlWithSpamCheck,
        bypassUnsubscription,
      );
    const blob = new Blob([htmlWithUnsubscription], {
      type: 'text/html; charset=utf-8',
    });
    iframe.current.src = URL.createObjectURL(blob);
  }, [html, misspellingWords, spamCheckResult, bypassUnsubscription]);
  const renderSpamResult = useMemo(() => {
    const groupByCategory = _.groupBy(spamCheckResult.spamWords, 'category');
    const renderScore = () => {
      if (spamCheckResult.score < 10) {
        return <span style={{ color: '#34d399' }}>Good</span>;
      }
      if (spamCheckResult.score < 20) {
        return <span style={{ color: '#fcd34c' }}>Medium</span>;
      }
      return <span style={{ color: '#f77171' }}>Poor</span>;
    };
    return (
      <>
        <b style={{ fontWeight: 900, marginBottom: 3 }}>
          Spam Score: {renderScore()}
        </b>
        <div className="flex space-x-2" style={{ marginBottom: 7 }}>
          {Object.entries(groupByCategory).map(([category, words]) => {
            const categoryObj = SPAM_WORD_CATEGORY.find(
              item => item.value === category,
            );
            if (!categoryObj) return null;
            const color = tinycolor(categoryObj.color);
            return (
              <Tag
                // color={categoryObj.color}
                key={category}
                style={{
                  marginRight: 0,
                  borderColor: color.toHexString(),
                  padding: '2px 7px',
                  backgroundColor: color.setAlpha(0.2).toRgbString(),
                }}
              >
                {categoryObj.title} ({words.length})
              </Tag>
            );
          })}
        </div>
      </>
    );
  }, [spamCheckResult]);
  return (
    <div className="flex" style={{ height: '40vh', flexDirection: 'column' }}>
      {renderSpamResult}
      <div className="flex flex-1">
        <iframe
          {...props}
          ref={iframe}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
    </div>
  );
};

export default RenderHTML;
